import React, { useState } from 'react'
import { Mail, ArrowRight, Clock, CheckCircle, Star } from 'lucide-react'

const FinalCTA: React.FC = () => {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [isSuccess, setIsSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return

    setIsLoading(true)
    try {
      const { subscribeEmail } = await import('../utils/api')
      const result = await subscribeEmail(email)
      setMessage(result.message)
      setIsSuccess(result.success)
      
      if (result.success) {
        setEmail('')
      }
    } catch (error) {
      setMessage('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.')
      setIsSuccess(false)
    } finally {
      setIsLoading(false)
    }
  }

  const benefits = [
    "무료 구독",
    "즉시 취소 가능", 
    "30분 → 3분 요약"
  ]

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 via-primary-900 to-purple-900 text-white relative overflow-hidden">
      {/* 배경 장식 */}
      <div className="absolute inset-0">
        <div className="absolute top-10 left-10 w-72 h-72 bg-primary-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-yellow-500/10 rounded-full blur-2xl"></div>
      </div>

      <div className="container-width section-padding relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-12">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-red-500/20 rounded-full mb-8 animate-pulse">
              <Clock className="w-12 h-12 text-red-400" />
            </div>
            
            <h2 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="text-red-400">지금이</span> 마지막 기회
            </h2>
            
            <p className="text-2xl md:text-3xl mb-8 text-gray-200 leading-relaxed">
              매주 진짜 AI 트렌드를 받아보세요
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm p-8 md:p-12 rounded-3xl border border-white/20 mb-12">
            <form onSubmit={handleSubmit} className="max-w-lg mx-auto mb-8">
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="flex-1">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="이메일을 입력하세요"
                    className="w-full px-6 py-4 text-lg rounded-xl border-2 border-white/20 bg-white/10 text-white placeholder-white/60 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/50 transition-all duration-200"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 text-gray-900 font-bold px-8 py-4 text-lg rounded-xl transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-yellow-400 flex items-center justify-center gap-2 min-w-[180px] shadow-lg"
                >
                  {isLoading ? (
                    <div className="w-6 h-6 border-2 border-gray-900/30 border-t-gray-900 rounded-full animate-spin"></div>
                  ) : (
                    <>
                      무료 구독하기
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
              
              {message && (
                <div className={`p-4 rounded-xl ${isSuccess ? 'bg-green-500/20 border border-green-400/30' : 'bg-red-500/20 border border-red-400/30'} mb-6`}>
                  <div className="flex items-center gap-2">
                    {isSuccess && <CheckCircle className="w-5 h-5 text-green-400" />}
                    <p className={`text-sm ${isSuccess ? 'text-green-100' : 'text-red-100'}`}>
                      {message}
                    </p>
                  </div>
                </div>
              )}
            </form>

            <div className="flex flex-wrap items-center justify-center gap-8 text-gray-300">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="font-medium">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
              <Star className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
              <h3 className="text-lg font-bold mb-2">프리미엄 품질</h3>
              <p className="text-gray-300 text-sm">전문가 수준의 분석과 번역</p>
            </div>
            <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
              <Mail className="w-12 h-12 text-blue-400 mx-auto mb-4" />
              <h3 className="text-lg font-bold mb-2">매주 정기 발송</h3>
              <p className="text-gray-300 text-sm">놓치지 않는 일정한 정보 공급</p>
            </div>
            <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
              <Clock className="w-12 h-12 text-green-400 mx-auto mb-4" />
              <h3 className="text-lg font-bold mb-2">시간 절약</h3>
              <p className="text-gray-300 text-sm">30분을 3분으로 압축</p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-500/20 to-orange-500/20 p-6 rounded-2xl border border-red-400/30">
            <p className="text-lg font-bold text-red-300 mb-2">
              ⚠️ 무료 구독은 선착순 제한입니다
            </p>
            <p className="text-gray-300">
              AI 시스템 운영 비용으로 인해 언제든 유료로 전환될 수 있습니다
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

export default FinalCTA
